import _ from "lodash";
import { ChangeEventHandler, useEffect, useState } from "react";
import * as yup from "yup";
import type { CashTargetCondition } from "../../../api/src/cash-settings/lib";
import type { HouseholdTitle } from "../../../api/src/contacts/lib";
import type { SecurityRestrictionTreatment } from "../../../api/src/models/models.service";
import { HOUSEHOLD_TITLES } from "../clients/contact/lib";
import { monthOptions, toISODateNoTimezone } from "./date";
import { phoneMask } from "./masks";

export const setNestedCollectionField = <TValue = any>(
  properties: (string | number)[],
  collection: any[],
  setCollection: (collection: any[]) => void,
  value: TValue,
  type = "text",
) => {
  const newObj = [...collection];

  _.set(
    newObj,
    properties.map((propName) => propName.toString()).join("."),
    type === "number" ? Number(value) : value,
  );

  setCollection(newObj);
};

export const setCollectionField = <TValue = any>(
  index: number,
  fieldName: string,
  collection: any[],
  setCollection: (collection: any[]) => void,
  value: TValue,
  type = "text",
) => {
  return setNestedCollectionField(
    [index, fieldName],
    collection,
    setCollection,
    value,
    type,
  );
};

export const setCollectionFieldHandler =
  (
    index: number,
    fieldName: string,
    collection: any[],
    setCollection: (collection: any[]) => void,
  ): ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  > =>
  (ev) => {
    setCollectionField(
      index,
      fieldName,
      collection,
      setCollection,
      ev.target.value,
      ev.target.type,
    );
  };

// The following debouncer implementation was adopted from a StackOverflow answer.
// https://stackoverflow.com/a/68228099/691501

type Deferred<TResult> = {
  promise: Promise<TResult> | null;
  cancel: (reason?: any) => void;
};

function deferred<TResult>(ms: number): Deferred<TResult> {
  let cancel: (reason?: any) => void = () => null;
  const promise = new Promise<TResult>((resolve, reject) => {
    cancel = reject;
    window.setTimeout(resolve, ms);
  });
  return { promise, cancel };
}

export function debouncer<TResult>(task: () => Promise<TResult>, ms: number) {
  let t: Deferred<TResult> = { promise: null, cancel: () => null };
  return [
    async () => {
      try {
        t.cancel();
        t = deferred(ms);
        await t.promise;
        await task();
      } catch {
        /* prevent memory leak */
      }
    },
    () => t.cancel(),
  ];
}

export function useDebounce<TResult>(task: () => Promise<TResult>, ms: number) {
  const [f, cancel] = debouncer(task, ms);
  useEffect(() => cancel);
  return [f, cancel];
}

export function useDebounceValue(value: any, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    // Cancel the timeout if value changes (also on delay change or unmount)
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Get the label value for a given schema field. This accepts an 'any' type
 * argument because the `describe` return type in Yup v1 is incorrect.
 *
 * @param schema - The schema value to describe
 */
export function getSchemaFieldLabel(schema: any): string {
  return schema.describe().label;
}

/**
 * The text for a field's on-screen label: the schema label, followed by
 * "(optional)" when the schema accepts an empty value (WCAG 2.2 3.3.2,
 * doc-to-dos-01a15eb2bb0b8302 R118-R120). A field is optional when it may be
 * left undefined or null. The schema label itself is left alone, because it
 * writes the error texts ("Name is a required field").
 */
export function getSchemaFieldFormLabel(
  schema: yup.ISchema<unknown> | yup.Reference,
): string {
  const description = schema.describe();
  if (!("optional" in description)) {
    return getSchemaFieldLabel(schema);
  }
  const label = description.label ?? "";
  return description.optional || description.nullable
    ? `${label} (optional)`
    : label;
}

export const undefinedStringSchema = yup
  .string()
  .transform((value, originalValue) => {
    return typeof originalValue === "undefined" ||
      originalValue === null ||
      originalValue === ""
      ? undefined
      : value;
  });

/**
 * Yup's number formatting error will override the required error, which is bad
 * from a UX perspective. Transform an empty value into a value that will
 * trigger the required field error instead of a type error.
 */
export const requiredNumberSchema = yup
  .number()
  .required()
  .transform((value, originalValue) => {
    return typeof originalValue === "undefined" ||
      originalValue === null ||
      originalValue === ""
      ? null
      : value;
  });
export const undefinedNumberSchema = yup
  .number()
  .transform((value, originalValue) => {
    return typeof originalValue === "undefined" ||
      originalValue === null ||
      originalValue === ""
      ? undefined
      : value;
  });
export const nullableNumberSchema = yup
  .number()
  .defined()
  .nullable()
  .transform((value, originalValue) => {
    return typeof originalValue === "undefined" ||
      originalValue === null ||
      originalValue === ""
      ? null
      : value;
  });
/**
 * An empty text input yields "", which a nullable column will store verbatim
 * rather than as NULL. Transform it to null, the way nullableNumberSchema does
 * for an empty number input.
 *
 * The default covers the other absent case: yup skips transforms entirely when
 * the raw value is undefined, which is what a controlled field that was never
 * given a value submits. Without it, `.defined()` rejects an untouched field.
 */
export const nullableStringSchema = yup
  .string()
  .defined()
  .nullable()
  .default(null)
  .transform((value, originalValue) => {
    return typeof originalValue === "undefined" ||
      originalValue === null ||
      originalValue === ""
      ? null
      : value;
  });
export const idOptionalSelectorSchema = yup
  .number()
  .transform((value, originalValue) => {
    return typeof originalValue === "undefined" ||
      originalValue === null ||
      originalValue === ""
      ? undefined
      : value;
  });
export const idsOptionalSelectorSchema = yup
  .array()
  .of(yup.number().required());

export function optionalStringValueTransform<TValue>(
  value: TValue,
  originalValue: string,
) {
  return originalValue === "" ? undefined : value;
}

export const monthSchema = yup.string().oneOf(monthOptions);

export const givenNameSchema = yup.string().label("First Name");
export const familyNameSchema = yup.string().label("Last Name");
export const additionalNameSchema = yup.string().label("Middle Name");
export const householdTitleSchema = undefinedStringSchema
  .oneOf(HOUSEHOLD_TITLES as HouseholdTitle[])
  .label("Household Title");
export const emailSchema = yup.string().email().label("Email");
export const phoneNumberSchema = yup
  .string()
  // `phoneMask.transform` is IMask's unmask pipe (`lib/masks.ts:12-16`), which
  // throws a plain `Error: value should be string` on anything that is not a
  // string, null included. yup appends transforms rather than prepending them,
  // so a `.nullable()` added at a call site can never run before this one and
  // cannot prevent the throw; the guard has to live here. It is additive: null
  // is the only input whose behaviour changes, and today that input throws for
  // every caller. Callers that add `.nullable()` now accept null; the rest get
  // an ordinary yup ValidationError in place of a raw Error that escapes the
  // resolver.
  .transform((value, originalValue) =>
    originalValue === null ? null : phoneMask.transform(value),
  )
  .matches(/^\+[0-9]{11}$/, {
    excludeEmptyString: true,
    message: "Phone number must be a valid US number, e.g. +1 (212)-555-1234",
  }) // Only support US phone numbers for now
  .label("Phone Number");
export const dateSchema = yup.date().transform(optionalStringValueTransform);
export const birthDateSchema = dateSchema
  .max(toISODateNoTimezone(new Date()))
  .label("Birthdate");
export const zipCodeSchema = yup
  .string()
  .matches(/^[0-9]{5}(-[0-9]{4})?$/, { excludeEmptyString: true })
  .label("Zip Code");

export const modelAssetCategorySchema = yup
  .string()
  .oneOf(["stock", "bond", "other", "cash"]);
export const modelRegionSchema = yup.string().oneOf(["US", "global"]);
export const modelSecurityWeightSchema = yup
  .number()
  .min(0)
  .max(100)
  .typeError(({ label }) => `${label} must be a number.`);

export const primarySecuritiesSchema = yup.array().of(
  yup.object({
    symbol: yup
      .string()
      .transform((value) => {
        return typeof value === "string" ? value.toUpperCase() : value;
      })
      .required()
      .label("Symbol"),
    weight: modelSecurityWeightSchema.required().label("Weight"),
  }),
);
export const secondarySecuritiesSchema = yup.array().of(
  yup.object({
    symbol: yup
      .string()
      .transform((value) => {
        return typeof value === "string" ? value.toUpperCase() : value;
      })
      .required()
      .label("Symbol"),
    treatment: yup
      .string()
      .oneOf(["like_primary", "hold_only", "sell_no_tax"])
      .required()
      .label("Treatment"),
  }),
);
export const sleevesSchema = yup.array().of(
  yup.object({
    id: yup.number().required().label("Sleeve"),
    weight: modelSecurityWeightSchema.required().label("Weight"),
  }),
);
export const securityGroupsSchema = yup.array().of(
  yup.object({
    id: yup.number().required().label("Security Group"),
    weight: modelSecurityWeightSchema.required().label("Weight"),
  }),
);

export const securityRestrictionTreatmentSchema = yup
  .string()
  .oneOf<SecurityRestrictionTreatment>(["hold_only", "sell_no_tax", "exclude"])
  .required();

export const securityRestrictionSchema = yup
  .array()
  .of(
    yup.object({
      symbol: yup
        .string()
        .transform((value) => {
          return typeof value === "string" ? value.toUpperCase() : value;
        })
        .required()
        .label("Symbol"),
      treatment: securityRestrictionTreatmentSchema.label("Treatment"),
    }),
  )
  .label("Security Restrictions");

export const cashConditionSchema = yup
  .string()
  .oneOf<CashTargetCondition>(["and", "or"])
  .default("and")
  .required();
